import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  forwardRef,
  Injector,
  Input,
  OnInit,
} from '@angular/core';
import {
  BehaviorSubject,
  distinctUntilChanged,
  map,
  Observable,
  shareReplay,
  Subject,
} from 'rxjs';
import {
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  TuiContextWithImplicit,
  TuiHandler,
  TuiLetModule,
  tuiPure,
} from '@taiga-ui/cdk';
import { AsyncPipe } from '@angular/common';
import {
  TuiComboBoxModule,
  TuiDataListWrapperModule,
  TuiMultiSelectModule,
} from '@taiga-ui/kit';
import {
  TuiDataListModule,
  TuiDropdownModule,
  TuiScrollbarModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import {
  CdkFixedSizeVirtualScroll,
  CdkVirtualForOf,
  CdkVirtualScrollViewport,
} from '@angular/cdk/scrolling';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-multi-select',
  standalone: true,
  imports: [
    TuiLetModule,
    AsyncPipe,
    ReactiveFormsModule,
    TuiMultiSelectModule,
    TuiDataListWrapperModule,
    TuiTextfieldControllerModule,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    TuiScrollbarModule,
    TuiDataListModule,
    CdkVirtualForOf,
    TuiComboBoxModule,
    TuiDropdownModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true,
    },
  ],
  templateUrl: './multi-select.component.html',
  styleUrl: './multi-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiSelectComponent<T> implements ControlValueAccessor, OnInit {
  formControl: FormControl;

  @Input({ required: false })
  itemText: keyof T = 'text' as keyof T;

  @Input({ required: false })
  displayValue: keyof T = '' as keyof T;

  @Input({ required: false })
  size: 's' | 'm' | 'l' = 'm';

  @Input({ required: false })
  label = '';

  @Input({ required: false })
  labelOutside = true;

  @Input({ required: false })
  componentClass = '';

  items$ = new BehaviorSubject<T[]>([]);
  itemsMap$ = new BehaviorSubject<Map<string, T>>(new Map());
  itemValue$ = new BehaviorSubject<keyof T>('value' as keyof T);

  private readonly search$ = new Subject<string>();
  itemHeight$ = this.items$.pipe(
    map(items => items.length),
    distinctUntilChanged(),
    map(length => this.getViewportHeight(length)),
    shareReplay(),
  );

  readonly stringify$: Observable<
    TuiHandler<TuiContextWithImplicit<string> | string, string>
  > = this.itemsMap$.pipe(
    map(map => (identity: TuiContextWithImplicit<string> | string) => {
      const identityValue = identity?.toString() || '';
      const item = map?.get(identityValue);
      if (!item) {
        return '';
      }
      const key = this.displayValue || this.itemValue;
      return item[key]?.toString() ?? '';
    }),
  );

  constructor(
    private readonly injector: Injector,
    private readonly destroyRef: DestroyRef,
  ) {}

  onChange: any = () => {};
  onTouch: any = () => {};
  getFullItemsMap(items: any[] | null) {
    const identity: Array<[string, any]> =
      items?.map(item => {
        const id = item[this.itemValue]?.toString();
        return [id, item];
      }) ?? [];
    return new Map(identity);
  }

  createIdentityMap() {
    this.items$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.itemsMap$.next(this.getFullItemsMap(this.items$.value));
    });
  }

  identityMatcher(t: string, e: string) {
    return t.toString()?.toLowerCase() === e?.toString().toLowerCase();
  }

  get items(): T[] {
    return this.items$.value;
  }

  @Input({
    required: true,
  })
  set items(value: T[] | null) {
    this.items$.next(value ?? []);
  }

  get itemValue() {
    return this.itemValue$.value;
  }

  @Input({ required: false })
  set itemValue(value: keyof T) {
    this.itemValue$.next(value);
  }

  @tuiPure
  get itemSize() {
    if (this.size === 'm') {
      return 40;
    }
    if (this.size === 's') {
      return 32;
    }
    return 40;
  }

  ngOnInit() {
    const ngControl = this.injector.get(NgControl);
    this.formControl = (ngControl as FormControlDirective).form;
    this.createIdentityMap();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  writeValue(value: any): void {}

  getViewportHeight(length: number | undefined) {
    const calculatedHeight = (length ?? 1) * 40 + 8;
    const maxHeight = 200;
    return Math.min(maxHeight, calculatedHeight);
  }
}
